import { Component } from '@theme/component';

/**
 * <brand-nav-overlay> — full-screen burgundy navigation overlay
 * (incommonwith-style, per client reference shots).
 *
 * Triggers: [data-nav-open="shop|collections|info"] links in the header
 * (rendered by blocks/_header-logo.liquid). The header stays visible above
 * the overlay; clicking the same trigger again, the ×, or Escape closes.
 * Active trigger gets [data-nav-active] (dot indicator via CSS).
 *
 * Owned by feature/homepage. No core theme files touched.
 */
class BrandNavOverlay extends Component {
  #current = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocClick);
    document.addEventListener('keydown', this.#onKey);
    document.addEventListener('pointerover', this.#onPointerOver);
    document.addEventListener('pointerout', this.#onPointerOut);
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocClick);
    document.removeEventListener('keydown', this.#onKey);
    document.removeEventListener('pointerover', this.#onPointerOver);
    document.removeEventListener('pointerout', this.#onPointerOut);
    window.removeEventListener('resize', this.#onResize);
  }

  /** hover'da açılış (scoping Q3a) — yalnız gerçek hover'lı cihazlarda,
      kısa niyet gecikmesiyle (kazara açılmaları önler). Tık davranışı değişmez. */
  #hoverTimer = null;
  #hoverMql = window.matchMedia('(hover: hover) and (pointer: fine)');

  #onPointerOver = (e) => {
    if (!this.#hoverMql.matches) return;
    const trigger = e.target.closest('[data-nav-open]');
    if (!trigger) return;
    clearTimeout(this.#hoverTimer);
    const panel = trigger.dataset.navOpen;
    if (this.#current === panel) return;
    this.#hoverTimer = setTimeout(() => this.open(panel), 90);
  };

  #onPointerOut = (e) => {
    if (e.target.closest('[data-nav-open]')) clearTimeout(this.#hoverTimer);
  };

  #onDocClick = (e) => {
    const trigger = e.target.closest('[data-nav-open]');
    if (trigger) {
      e.preventDefault();
      const panel = trigger.dataset.navOpen;
      if (this.#current === panel) this.close();
      else this.open(panel);
      return;
    }
    if (e.target.closest('[data-nav-close]')) {
      e.preventDefault();
      this.close();
      return;
    }
    // Mobil drawer: akordeon satırları — yükseklik kaydırmalı animasyon
    // (grid-rows 0fr→1fr, CSS'te), + ↔ −, tek seferde bir akordeon açık (ICW)
    const acc = e.target.closest('[data-acc-toggle]');
    if (acc) {
      const sub = acc.nextElementSibling;
      if (!sub) return;
      const willOpen = !sub.classList.contains('is-open');
      for (const other of this.querySelectorAll('.afo-overlay__mobile-sub.is-open')) {
        if (other === sub) continue;
        other.classList.remove('is-open');
        const btn = other.previousElementSibling;
        btn?.setAttribute('aria-expanded', 'false');
        const os = btn?.querySelector('span');
        if (os) os.textContent = '+';
      }
      sub.classList.toggle('is-open', willOpen);
      acc.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      const sp = acc.querySelector('span');
      if (sp) sp.textContent = willOpen ? '−' : '+';
      return;
    }
    // Collections: alttaki ok çifti kartları bir kart genişliği kaydırır;
    // kenardaki ok gri/tıklanamaz (ICW referansı, #updateCardArrows)
    const cardsNav = e.target.closest('[data-cards-next], [data-cards-prev]');
    if (cardsNav) {
      const scroller = this.querySelector('.afo-overlay__cards');
      const card = scroller?.querySelector('.afo-overlay__card');
      if (scroller && card) {
        const dir = cardsNav.hasAttribute('data-cards-prev') ? -1 : 1;
        scroller.scrollBy({ left: dir * (card.offsetWidth + 8), behavior: 'smooth' });
      }
      return;
    }
    // panelin dışına (karartılmış alta) tıklama kapatır
    if (this.#current && e.target === this) this.close();
  };

  /** menü açıkken kaydırma girişimi menüyü kapatır (referans davranışı) */
  #onWheel = (e) => {
    if (this.#current && Math.abs(e.deltaY) > 10) this.close();
  };
  #touchY = 0;
  #touchInPanel = false;
  #onTouchStart = (e) => {
    this.#touchY = e.touches[0]?.clientY ?? 0;
    // panel içinde başlayan dokunuş = içerik kaydırma (mobil drawer), menüyü kapatma
    this.#touchInPanel = !!e.target.closest('.afo-overlay__inner');
  };
  #onTouchMove = (e) => {
    if (this.#touchInPanel) return;
    if (this.#current && Math.abs((e.touches[0]?.clientY ?? 0) - this.#touchY) > 24) this.close();
  };

  #onKey = (e) => {
    if (e.key === 'Escape' && this.#current) this.close();
  };

  #closeTimer = null;

  #updateCardArrows = () => {
    const scroller = this.querySelector('.afo-overlay__cards');
    const prev = this.querySelector('[data-cards-prev]');
    const next = this.querySelector('[data-cards-next]');
    if (!scroller || !prev || !next) return;
    prev.disabled = scroller.scrollLeft <= 1;
    next.disabled = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 1;
  };

  open(panel) {
    clearTimeout(this.#closeTimer);
    // Info listesi TAM tetikleyicinin altında dursun — morph durumuna göre
    // tetikleyici kaydığından konum açılış anında ölçülür
    if (panel === 'info') this.#syncInfoLeft();
    for (const p of this.querySelectorAll('.afo-overlay__panel')) {
      p.hidden = p.dataset.panel !== panel;
    }
    this.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
    document.documentElement.classList.add('afo-overlay-open');
    this.#current = panel;
    this.#markTriggers();
    window.addEventListener('wheel', this.#onWheel, { passive: true });
    window.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    window.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    if (panel === 'collections') {
      this.#updateCardArrows();
      this.querySelector('.afo-overlay__cards')?.addEventListener('scroll', this.#updateCardArrows, { passive: true });
    }
  }

  close() {
    this.classList.remove('is-open');
    this.#closeTimer = setTimeout(() => { this.hidden = true; }, 220); // kapanış animasyonu bitince gizle
    document.documentElement.classList.remove('afo-overlay-open');
    this.#current = null;
    this.#markTriggers();
    window.removeEventListener('wheel', this.#onWheel);
    window.removeEventListener('touchstart', this.#onTouchStart);
    window.removeEventListener('touchmove', this.#onTouchMove);
  }

  #syncInfoLeft = () => {
    const t = document.querySelector('[data-nav-open="info"]');
    if (t) this.style.setProperty('--afo-info-left', Math.round(t.getBoundingClientRect().left) + 'px');
  };

  #onResize = () => {
    if (this.querySelector('.afo-overlay__panel[data-panel="info"]:not([hidden])')) this.#syncInfoLeft();
  };

  #markTriggers() {
    for (const t of document.querySelectorAll('[data-nav-open]')) {
      t.toggleAttribute('data-nav-active', t.dataset.navOpen === this.#current);
      t.setAttribute('aria-expanded', t.dataset.navOpen === this.#current ? 'true' : 'false');
    }
  }
}

if (!customElements.get('brand-nav-overlay')) {
  customElements.define('brand-nav-overlay', BrandNavOverlay);
}
